"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import { Probability } from "@/components/tools/stats-distributions/charts";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";

/** One line of a continuous-distribution answer table. */
export interface ProbabilityRow {
  /** Mathematical notation, the same in every language: P(X ≤ 6). */
  notation: string;
  /** The plain-language reading, from the calling widget's strings. */
  meaning: string;
  /** A probability (four decimals, or scientific for tiny values) or a density. */
  value: number;
  /** A density is a number to five significant digits, not a probability. */
  kind?: "probability" | "density";
}

/**
 * The notation / meaning / value table the continuous distribution calculators share.
 * Column headings come in as props; nothing here is English.
 */
export function ProbabilityRows({
  rows,
  labels,
  fmt,
}: {
  rows: readonly ProbabilityRow[];
  labels: { probability: string; meaning: string; value: string };
  fmt: NumberFormatter;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {labels.probability}
            </th>
            <th className={TH} scope="col">
              {labels.meaning}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {labels.value}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.notation}>
              <td className={`${TD} whitespace-nowrap font-mono`}>
                {row.notation}
              </td>
              <td className={`${TD} text-muted-foreground`}>{row.meaning}</td>
              <td className={`${TD} text-right font-mono`}>
                {row.kind === "density" ? (
                  fmt(row.value, { maximumSignificantDigits: 5 })
                ) : (
                  <Probability fmt={fmt} value={row.value} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
